import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminActivity } from '../../services/authApi';
import { canonicalBuildPath } from '../../utils/buildRoutes';
import './ActivityLog.css';

// How many rows one fetch brings back. The feed is read top-down, so a page
// that overflows one screen is already more than the first look needs.
const PAGE_SIZE = 60;
// Order of the filter chips. `all` first, then the kinds by how often they fire.
const KINDS = ['all', 'errors', 'visit', 'gen_start', 'build_save', 'build_like', 'login', 'signup', 'cap_hit'];

const T = {
    title: { fr: 'Admin · Journal', en: 'Admin · Activity', de: 'Admin · Aktivität', es: 'Admin · Actividad', pt: 'Admin · Atividade' },
    loading: { fr: 'Chargement du journal...', en: 'Loading activity...', de: 'Aktivität wird geladen...', es: 'Cargando actividad...', pt: 'Carregando atividade...' },
    error: { fr: 'Impossible de charger le journal.', en: 'Could not load the activity log.', de: 'Aktivitätsprotokoll konnte nicht geladen werden.', es: 'No se ha podido cargar el registro.', pt: 'Não foi possível carregar o registro.' },
    empty: { fr: 'Aucune activité pour le moment.', en: 'No activity yet.', de: 'Noch keine Aktivität.', es: 'Aún no hay actividad.', pt: 'Ainda não há atividade.' },
    more: { fr: 'Charger plus', en: 'Load more', de: 'Mehr laden', es: 'Cargar más', pt: 'Carregar mais' },
    refresh: { fr: 'Rafraîchir', en: 'Refresh', de: 'Aktualisieren', es: 'Actualizar', pt: 'Atualizar' },
    // Date picker: "read the feed as it stood at the end of this day".
    dateLabel: { fr: 'Jusqu\'au', en: 'Up to', de: 'Bis zum', es: 'Hasta el', pt: 'Até' },
    today: { fr: "Aujourd'hui", en: 'Today', de: 'Heute', es: 'Hoy', pt: 'Hoje' },
    retention: {
        fr: 'Les événements sont conservés {n} jours.',
        en: 'Events are kept for {n} days.',
        de: 'Ereignisse werden {n} Tage aufbewahrt.',
        es: 'Los eventos se conservan {n} días.',
        pt: 'Os eventos são mantidos por {n} dias.',
    },
    // Filter chips
    filterGroup: { fr: 'Filtre d\'activité', en: 'Activity filter', de: 'Aktivitätsfilter', es: 'Filtro de actividad', pt: 'Filtro de atividade' },
    f_all: { fr: 'Tout', en: 'All', de: 'Alle', es: 'Todo', pt: 'Tudo' },
    f_visit: { fr: 'Visites', en: 'Visits', de: 'Besuche', es: 'Visitas', pt: 'Visitas' },
    f_gen_start: { fr: 'Générations', en: 'Generations', de: 'Generierungen', es: 'Generaciones', pt: 'Gerações' },
    f_build_save: { fr: 'Stuffs', en: 'Builds', de: 'Builds', es: 'Builds', pt: 'Builds' },
    f_build_like: { fr: 'Likes', en: 'Likes', de: 'Likes', es: 'Likes', pt: 'Curtidas' },
    f_login: { fr: 'Connexions', en: 'Logins', de: 'Logins', es: 'Conexiones', pt: 'Logins' },
    f_signup: { fr: 'Inscriptions', en: 'Signups', de: 'Registrierungen', es: 'Registros', pt: 'Cadastros' },
    f_cap_hit: { fr: 'Blocages', en: 'Blocks', de: 'Blockierungen', es: 'Bloqueos', pt: 'Bloqueios' },
    // Not a kind but a group (client + server), resolved by the backend.
    f_errors: { fr: 'Erreurs', en: 'Errors', de: 'Fehler', es: 'Errores', pt: 'Erros' },
    // Sentences: "<actor> <verb> [<target>]"
    v_login: { fr: "s'est connecté avec Discord", en: 'connected with Discord', de: 'hat sich mit Discord verbunden', es: 'se conectó con Discord', pt: 'conectou-se com o Discord' },
    v_signup: { fr: 'a créé un compte avec Discord', en: 'created an account with Discord', de: 'hat ein Konto mit Discord erstellt', es: 'creó una cuenta con Discord', pt: 'criou uma conta com o Discord' },
    // One row per visit, not per page: the beacon only fires again once the
    // visitor has been away long enough to count as coming back.
    v_visit: { fr: 'est passé sur le site', en: 'visited the site', de: 'hat die Seite besucht', es: 'visitó el sitio', pt: 'visitou o site' },
    v_gen_start: { fr: 'a lancé une génération', en: 'started a generation', de: 'hat eine Generierung gestartet', es: 'inició una generación', pt: 'iniciou uma geração' },
    v_build_save: { fr: 'a créé le stuff', en: 'created build', de: 'hat den Build erstellt', es: 'creó la build', pt: 'criou a build' },
    v_build_like: { fr: 'a liké le stuff', en: 'liked build', de: 'hat den Build geliked', es: 'dio like a la build', pt: 'curtiu a build' },
    // In practice always the guest anti-bot ceiling (chip: `day`/`week`), so
    // "blocked" rather than a sentence naming a ceiling the chip already names.
    // Kept generic because the quota gate can also fire for a logged-in user.
    v_cap_hit: { fr: 'a été bloqué par le plafond de générations', en: 'was blocked by the generation cap', de: 'wurde vom Generierungslimit blockiert', es: 'fue bloqueado por el tope de generaciones', pt: 'foi bloqueado pelo limite de gerações' },
    // Failures. The actor is the visitor whose browser threw, or «server».
    v_client_error: { fr: 'a rencontré une erreur', en: 'hit a browser error', de: 'hatte einen Browser-Fehler', es: 'encontró un error', pt: 'encontrou um erro' },
    v_server_error: { fr: 'a renvoyé une erreur', en: 'raised an error', de: 'hat einen Fehler ausgelöst', es: 'produjo un error', pt: 'gerou um erro' },
};

const tr = (key, lang) => (T[key] && (T[key][lang] || T[key].en)) || key;

/** "14:32" for today, "12 août 14:32" otherwise — the feed is read newest-first,
 *  so most rows only need the time of day. */
function formatWhen(iso, lang) {
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return '—';
    const time = at.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    const isToday = at.toDateString() === new Date().toDateString();
    if (isToday) return time;
    const day = at.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
    return `${day} ${time}`;
}

/** `YYYY-MM-DD` in the reader's own timezone — what an <input type="date">
 *  speaks, and the timezone the rows are rendered in. */
function localDayValue(at) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/** The picked day's last instant, absolute. The backend stores UTC, so sending
 *  a bare date would cut the day at the wrong hour for everyone but UTC. */
function endOfLocalDay(day) {
    if (!day) return null;
    const [y, m, d] = day.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

/** Oldest day still worth offering: past retention there is nothing to find. */
function earliestDayValue(retentionDays) {
    if (!retentionDays) return undefined;
    const at = new Date();
    at.setDate(at.getDate() - retentionDays);
    return localDayValue(at);
}

function ActorName({ event, lang }) {
    if (event.is_anon || !event.actor_slug) {
        return <span className="activity-actor anon">{event.actor}</span>;
    }
    return (
        <Link className="activity-actor" to={`/${lang}/community/${encodeURIComponent(event.actor_slug)}`}>
            {event.actor}
        </Link>
    );
}

function TargetName({ event, lang }) {
    if (!event.target) return null;
    if (!event.target_slug || !event.target_code) {
        return <span className="activity-target">{event.target}</span>;
    }
    return (
        <Link
            className="activity-target"
            to={canonicalBuildPath(lang, event.target_slug, event.target_code)}
        >
            {event.target}
        </Link>
    );
}

function ActivityRow({ event, lang }) {
    return (
        <li className="activity-row">
            <span className={`activity-dot ${event.kind}`} aria-hidden="true" />
            <time className="activity-time" dateTime={event.at}>{formatWhen(event.at, lang)}</time>
            <span className="activity-text">
                <ActorName event={event} lang={lang} />
                {' '}
                {tr(`v_${event.kind}`, lang)}
                {event.target ? ' ' : null}
                <TargetName event={event} lang={lang} />
            </span>
            {event.detail ? <span className="activity-chip">{event.detail}</span> : null}
        </li>
    );
}

/** Reverse-chronological feed of what people do in the app. Paged by cursor:
 *  new events land at the top while you read, and an offset would skip rows. */
function ActivityLog({ lang }) {
    const [events, setEvents] = useState([]);
    const [meta, setMeta] = useState(null);
    const [status, setStatus] = useState('loading');
    const [kind, setKind] = useState('all');
    // '' = the live feed. A day sends the reading head back to that evening.
    const [day, setDay] = useState('');

    // One fetch path for all four triggers (filter, date, refresh, load more):
    // `before` is what makes it a page-2 request, and appending vs replacing
    // follows from it.
    const load = useCallback((nextKind, nextDay, before) => {
        let cancelled = false;
        setStatus('loading');
        fetchAdminActivity({ limit: PAGE_SIZE, kind: nextKind, before, until: endOfLocalDay(nextDay) })
            .then((payload) => {
                if (cancelled) return;
                const rows = payload.events || [];
                setEvents((prev) => (before ? [...prev, ...rows] : rows));
                setMeta(payload);
                setStatus('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                console.warn('fetchAdminActivity failed:', err);
                setStatus('error');
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => load(kind, day), [kind, day, load]);

    const retentionNote = meta?.retention_days
        ? tr('retention', lang).replace('{n}', String(meta.retention_days))
        : null;

    return (
        <section className="activity-log">
            <div className="activity-header">
                <h2 className="activity-title">{tr('title', lang)}</h2>
                <div className="activity-header-tools">
                    <label className="activity-date">
                        <span className="activity-date-label">{tr('dateLabel', lang)}</span>
                        <input
                            type="date"
                            value={day}
                            max={localDayValue(new Date())}
                            min={earliestDayValue(meta?.retention_days)}
                            onChange={(event) => setDay(event.target.value)}
                        />
                    </label>
                    {day ? (
                        <button
                            type="button"
                            className="activity-refresh"
                            onClick={() => setDay('')}
                        >
                            {tr('today', lang)}
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="activity-refresh"
                        onClick={() => load(kind, day)}
                    >
                        {tr('refresh', lang)}
                    </button>
                </div>
            </div>

            <div className="activity-filters" role="group" aria-label={tr('filterGroup', lang)}>
                {KINDS.map((k) => (
                    <button
                        key={k}
                        type="button"
                        className={`activity-filter-btn${k === kind ? ' active' : ''}`}
                        onClick={() => setKind(k)}
                    >
                        {tr(`f_${k}`, lang)}
                    </button>
                ))}
            </div>

            {status === 'error' && <p className="activity-empty">{tr('error', lang)}</p>}
            {status === 'loading' && events.length === 0 && (
                <p className="activity-empty">{tr('loading', lang)}</p>
            )}
            {status === 'ready' && events.length === 0 && (
                <p className="activity-empty">{tr('empty', lang)}</p>
            )}

            {events.length > 0 && (
                <ul className="activity-list">
                    {events.map((event) => (
                        <ActivityRow key={event.id} event={event} lang={lang} />
                    ))}
                </ul>
            )}

            {meta?.has_more ? (
                <button
                    type="button"
                    className="activity-more"
                    onClick={() => load(kind, day, meta.next_before)}
                    disabled={status === 'loading'}
                >
                    {tr('more', lang)}
                </button>
            ) : null}

            {retentionNote ? <p className="activity-note">{retentionNote}</p> : null}
        </section>
    );
}

export default ActivityLog;
